import { useState, FormEvent, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchHistoryItem } from '../hooks/useSearchHistory';
import SearchHistory from './SearchHistory';

type SearchType = 'semantic' | 'hybrid' | 'keyword';

interface SearchBarProps {
  onSearch: (query: string, type: SearchType) => void;
  isLoading: boolean;
  defaultType?: SearchType;
  defaultQuery?: string;
  history?: SearchHistoryItem[];
  onHistorySelect?: (query: string, type: SearchType) => void;
  onHistoryRemove?: (timestamp: number) => void;
  onHistoryClear?: () => void;
}

export default function SearchBar({
  onSearch,
  isLoading,
  defaultType = 'semantic',
  defaultQuery = '',
  history = [],
  onHistorySelect,
  onHistoryRemove,
  onHistoryClear,
}: SearchBarProps) {
  const [query, setQuery] = useState(defaultQuery);
  const [searchType, setSearchType] = useState<SearchType>(defaultType);
  const [showHistory, setShowHistory] = useState(false);
  const containerRef = useRef<HTMLFormElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (defaultQuery) setQuery(defaultQuery);
  }, [defaultQuery]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowHistory(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      onSearch(query, searchType);
      setShowHistory(false);
    }
  };

  const handleHistorySelect = (historyQuery: string, type: SearchType) => {
    setQuery(historyQuery);
    setSearchType(type);
    if (onHistorySelect) {
      onHistorySelect(historyQuery, type);
    }
    setShowHistory(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setShowHistory(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full" ref={containerRef}>
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 p-4 relative">
        {/* Search input */}
        <div className="flex items-center gap-3">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <svg
                className="w-5 h-5 text-slate-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => history.length > 0 && setShowHistory(true)}
              onKeyDown={handleKeyDown}
              placeholder={t('search.placeholder')}
              className="w-full pl-12 pr-4 py-3 text-lg border-0 bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-slate-800 transition-all placeholder:text-slate-400"
              disabled={isLoading}
            />
          </div>
          <button
            type="submit"
            disabled={isLoading || !query.trim()}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 dark:disabled:bg-slate-600 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span className="hidden sm:inline">{t('search.searching')}</span>
              </>
            ) : (
              <span>{t('search.submit')}</span>
            )}
          </button>
        </div>

        {/* Search type toggle */}
        <div className="mt-4 space-y-2">
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-sm text-slate-500 dark:text-slate-400">{t('search.typeLabel')}:</span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setSearchType('semantic')}
                className={`px-4 py-1.5 text-sm rounded-full transition-colors ${
                  searchType === 'semantic'
                    ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 font-medium'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                }`}
              >
                {t('search.semantic')}
              </button>
            </div>
          </div>
          {/* Search type explanation */}
          <div className="flex items-start gap-2 p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg text-xs text-slate-600 dark:text-slate-400">
            <svg className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>
              {t('search.semanticDescription')}
            </span>
          </div>
        </div>

        {/* Search history dropdown */}
        {onHistorySelect && onHistoryRemove && onHistoryClear && (
          <SearchHistory
            history={history}
            onSelect={handleHistorySelect}
            onRemove={onHistoryRemove}
            onClear={onHistoryClear}
            isOpen={showHistory}
            onClose={() => setShowHistory(false)}
          />
        )}
      </div>
    </form>
  );
}
